import { useEffect, useMemo, useState } from 'react';
import { AffilmasterApiError, getAffilmasterDashboard, type AffilmasterDashboard } from '../api/affilmaster';
import { useFeather } from '../shell/useFeather';
import { getCrmStats, type CrmStats } from '../api/crm';
import { useAuth } from '../auth/AuthContext';
import { stagesForSlug } from '../crm/crm-stages';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { MetricsDashboard, type MetricsDashboardMetric } from '../components/arc/metrics-dashboard/metrics-dashboard';

const MONTHS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
const MONTHS_SHORT = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'];
const count = (value: number) => Number(value || 0).toLocaleString('cs-CZ', { maximumFractionDigits: 0 });
const money = (value: number, currency: 'CZK' | 'EUR') => `${count(value)} ${currency === 'CZK' ? 'Kč' : '€'}`;
const monthLabel = (ym: string) => {
  const [year, month] = ym.split('-').map(Number);
  return `${MONTHS[(month || 1) - 1]} ${year}`;
};
const monthShort = (ym: string) => {
  const [year, month] = ym.split('-').map(Number);
  return `${MONTHS_SHORT[(month || 1) - 1]} ${String(year).slice(2)}`;
};

export function AffilmasterDashboardPage() {
  const { me } = useAuth();
  const canReadCrm = !!me?.canAccessAllSpacesInTenant || !!me?.permissionKeys?.includes('crm:read');
  const [data, setData] = useState<AffilmasterDashboard | null>(null);
  const [leads, setLeads] = useState<CrmStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAffilmasterDashboard()
      .then((next) => {
        if (!cancelled) {
          setData(next);
          setError(null);
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof AffilmasterApiError ? reason.message : 'Dashboard se nepodařilo načíst.');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!canReadCrm) {
      setLeads(null);
      return () => { cancelled = true; };
    }
    getCrmStats('all')
      .then((next) => { if (!cancelled) setLeads(next); })
      .catch(() => { if (!cancelled) setLeads(null); });
    return () => { cancelled = true; };
  }, [canReadCrm]);

  useFeather([data, leads]);

  const performanceMetrics = useMemo<MetricsDashboardMetric[]>(() => {
    if (!data) return [];
    const rows = data.financeTrend.labels.map((label, index) => ({
      label: monthShort(label),
      cz: data.financeTrend.cz[index]!,
      sk: data.financeTrend.sk[index]!,
    }));
    const makeMetric = (
      key: string,
      label: string,
      field: 'commission' | 'deposits_volume' | 'ngr_volume' | 'registrations',
      monetary: boolean,
    ): MetricsDashboardMetric => {
      const total = (row: typeof rows[number]) => row.cz[field] + row.sk[field] * (monetary ? data.rate : 1);
      const points = rows.map((row, index) => ({
        label: row.label,
        value: total(row),
        previous: index > 0 ? total(rows[index - 1]!) : null,
      }));
      const value = points.at(-1)?.value ?? 0;
      const previous = points.at(-2)?.value ?? null;
      const delta = previous ? ((value - previous) / previous) * 100 : null;
      return {
        key,
        label,
        value,
        delta,
        deltaLabel: delta == null ? 'bez srovnání' : `${Math.abs(delta).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} %`,
        deltaDirection: delta == null || delta === 0 ? 'neutral' : delta > 0 ? 'up' : 'down',
        points,
        format: monetary ? (next) => money(next, 'CZK') : count,
        axisFormat: monetary ? (next) => `${count(next / 1000)} tis.` : count,
      };
    };
    return [
      makeMetric('commission', 'Provize', 'commission', true),
      makeMetric('deposits', 'Vklady', 'deposits_volume', true),
      makeMetric('ngr', 'NGR', 'ngr_volume', true),
      makeMetric('registrations', 'Registrace', 'registrations', false),
    ];
  }, [data]);

  if (error) return <div className="prototype-notice prototype-notice-error"><i data-feather="alert-circle" /><div><strong>Dashboard se nepodařilo načíst</strong><span>{error}</span></div></div>;
  if (!data) return <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>;

  const registrations = data.cz.month.registrations + data.sk.month.registrations;
  const ftd = data.cz.month.ftd_count + data.sk.month.ftd_count;
  const active = data.activity.active;
  const conversion = registrations ? (ftd / registrations) * 100 : 0;
  const affiliateLeads = leads?.byKind.affiliate?.total ?? 0;
  const advertiserLeads = leads?.byKind.inzerent?.total ?? 0;
  const typedLeads = affiliateLeads + advertiserLeads;
  const affiliateShare = typedLeads ? (affiliateLeads / typedLeads) * 100 : 50;

  return (
    <section className="am-home p1-arc-scope">
      <header className="prototype-page-head am-home-head">
        <div>
          <div className="prototype-eyebrow">AFFILMASTER</div>
          <h1>Dashboard</h1>
          <p>Výkon affiliate sítě za {monthLabel(data.ym)}</p>
        </div>
        <div className="am-home-period"><i data-feather="calendar" /><span>{monthLabel(data.ym)}</span></div>
      </header>

      <div className="am-home-kpis">
        <MetricCard label="Aktivní affiliates" value={active} context={`z ${count(data.activity.total)} celkem`} />
        <MetricCard label="Registrace" value={registrations} context={`${count(data.cz.month.registrations)} CZ · ${count(data.sk.month.registrations)} SK`} />
        <MetricCard label="FTD" value={ftd} context={`${conversion.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} % z registrací`} />
        <MetricCard label="Provize" value={data.cz.month.commission + data.sk.month.commission * data.rate} suffix=" Kč" context="CZ a SK přepočteno" />
      </div>

      <div className="am-home-analytics">
        <MetricsDashboard
          title="Výkon v čase"
          subtitle={`Posledních ${data.financeTrend.labels.length} měsíců · CZ a SK přepočteno do Kč`}
          metrics={performanceMetrics}
          defaultMetric="commission"
          compareLabel="Předchozí měsíc"
          compareSeriesLabel="Předchozí měsíc"
          breakdowns={[
            {
              title: `Česko · ${monthLabel(data.ym)}`,
              column: 'Tento měsíc',
              rows: [
                { name: 'Vklady', value: data.cz.month.deposits_volume, format: (value) => money(value, 'CZK') },
                { name: 'NGR', value: data.cz.month.ngr_volume, format: (value) => money(value, 'CZK') },
                { name: 'Provize', value: data.cz.month.commission, format: (value) => money(value, 'CZK') },
              ],
            },
            {
              title: `Slovensko · ${monthLabel(data.ym)}`,
              column: 'Tento měsíc',
              rows: [
                { name: 'Vklady', value: data.sk.month.deposits_volume, format: (value) => money(value, 'EUR') },
                { name: 'NGR', value: data.sk.month.ngr_volume, format: (value) => money(value, 'EUR') },
                { name: 'Provize', value: data.sk.month.commission, format: (value) => money(value, 'EUR') },
              ],
            },
          ]}
          ariaLabel="Vývoj výkonu Affilmaster sítě"
        />
      </div>

      {leads && leads.total > 0 && (
        <article className="prototype-panel am-home-leads" aria-label="Přehled CRM leadů">
          <div className="am-home-leads-overview">
            <span className="am-home-leads-icon"><i data-feather="briefcase" /></span>
            <div className="am-home-leads-copy">
              <span>CRM PIPELINE</span>
              <h2>Leady</h2>
              <p><strong>{count(leads.total)}</strong> obchodních příležitostí</p>
            </div>
          </div>

          <div className="am-home-leads-stages" aria-label="Leady podle fáze">
            {stagesForSlug('affilmaster').map((stage) => (
              <div className="am-home-lead-stage" key={stage.key}>
                <span className="am-home-lead-label"><i style={{ backgroundColor: stage.color }} />{stage.label}</span>
                <strong>{count(leads.byStatus[stage.key] ?? 0)}</strong>
                <small>z {count(leads.total)} celkem</small>
              </div>
            ))}
          </div>

          <div className="am-home-leads-mix">
            <span>Typ příležitosti</span>
            <div className="am-home-leads-kind"><span>Affiliate</span><strong>{count(affiliateLeads)}</strong></div>
            <div className="am-home-leads-kind"><span>Inzerent</span><strong>{count(advertiserLeads)}</strong></div>
            <div className="am-home-leads-split" aria-hidden="true"><span style={{ width: `${affiliateShare}%` }} /></div>
          </div>
        </article>
      )}
    </section>
  );
}
